import { useEffect, useState } from 'react'
import { cn } from './cn'

export interface PaginationProps {
  page: number
  totalPages: number
  from: number
  to: number
  total: number
  onPageChange: (nextPage: number) => void
  perPage?: number
  onPerPageChange?: (nextPerPage: number) => void
  perPageOptions?: number[]
  jump?: boolean | 'auto'
  firstLast?: boolean
  alwaysShowRange?: boolean
  className?: string
}

export function Pagination({ page, totalPages, from, to, total, onPageChange, perPage, onPerPageChange, perPageOptions, jump = 'auto', firstLast = false, alwaysShowRange = false, className = '' }: PaginationProps) {
  const [jumpValue, setJumpValue] = useState(String(page))
  const showJump = jump === true || (jump === 'auto' && totalPages > 5)

  useEffect(() => {
    setJumpValue(String(page))
  }, [page])

  if (totalPages <= 1 && !alwaysShowRange) return null

  function submitJump() {
    const parsed = Number(jumpValue)
    if (!Number.isFinite(parsed)) return
    onPageChange(clampPage(Math.trunc(parsed), totalPages))
  }

  return (
    <nav
      data-pagination
      aria-label="Pagination"
      className={cn(
        'flex min-w-0 max-w-full flex-wrap items-center justify-between gap-x-3 gap-y-2 overflow-x-auto overscroll-x-contain px-2 py-3 text-xs text-ink-muted shadow-scroll-hint',
        className,
      )}
    >
      <p className="shrink-0 whitespace-nowrap">Showing {from} to {to} of {total}</p>
      <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
        {onPerPageChange && perPageOptions && typeof perPage === 'number' && (
          <select
            aria-label="Rows per page"
            value={perPage}
            onChange={(e) => onPerPageChange(parseInt(e.target.value, 10))}
            className="text-xs border border-line text-ink-body rounded-control px-2 min-h-tap focus-visible:outline-none focus-visible:shadow-focus"
          >
            {perPageOptions.map((opt) => (
              <option key={opt} value={opt}>{opt} / page</option>
            ))}
          </select>
        )}
        {showJump && (
          <label className="flex shrink-0 items-center gap-1 whitespace-nowrap text-xs text-ink-muted">
            Page
            <input
              type="number"
              aria-label="Page number"
              data-exact-set-name="Page number"
              min={1}
              max={totalPages}
              value={jumpValue}
              onChange={(event) => setJumpValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  submitJump()
                }
              }}
              className="min-h-tap w-16 rounded-control border border-line px-2 text-xs text-ink-body focus-visible:outline-none focus-visible:shadow-focus"
            />
            <button
              type="button"
              onClick={submitJump}
              className="inline-flex min-h-tap items-center justify-center rounded-control border border-line px-2 text-xs text-ink-body hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus"
            >
              Go
            </button>
          </label>
        )}
        <div className="flex shrink-0 flex-nowrap gap-1">
          {firstLast && (
            <button
              data-exact-set-name="First page"
              type="button"
              aria-label={`Go to first page, page 1 of ${totalPages}`}
              onClick={() => onPageChange(1)}
              disabled={page === 1}
              className="inline-flex items-center justify-center min-h-tap px-3 border border-line text-ink-body rounded-control text-xs hover:bg-whisper disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:shadow-focus"
            >
              First
            </button>
          )}
          <button
            data-exact-set-name="Previous page"
            type="button"
            aria-label={`Go to previous page, page ${Math.max(1, page - 1)} of ${totalPages}`}
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page === 1}
            className="inline-flex items-center justify-center min-h-tap px-3 border border-line text-ink-body rounded-control text-xs hover:bg-whisper disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:shadow-focus"
          >
            Prev
          </button>
          <span className="px-2 py-1 text-xs text-ink-muted" aria-current="page" aria-live="polite">Page {page} of {totalPages}</span>
          <button
            data-exact-set-name="Next page"
            type="button"
            aria-label={`Go to next page, page ${Math.min(totalPages, page + 1)} of ${totalPages}`}
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="inline-flex items-center justify-center min-h-tap px-3 border border-line text-ink-body rounded-control text-xs hover:bg-whisper disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:shadow-focus"
          >
            Next
          </button>
          {firstLast && (
            <button
              data-exact-set-name="Last page"
              type="button"
              aria-label={`Go to last page, page ${totalPages} of ${totalPages}`}
              onClick={() => onPageChange(totalPages)}
              disabled={page === totalPages}
              className="inline-flex items-center justify-center min-h-tap px-3 border border-line text-ink-body rounded-control text-xs hover:bg-whisper disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:shadow-focus"
            >
              Last
            </button>
          )}
        </div>
      </div>
    </nav>
  )
}

function clampPage(page: number, totalPages: number): number {
  return Math.min(Math.max(page, 1), Math.max(totalPages, 1))
}
